import { Badge, Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePermission } from "@how/app-kit";
import { DataTable, PageHeader } from "@how/ui";
import { formFields, useCreateForm, useForms, type FormRow } from "./form-queries";
import { WebsiteTabs } from "./WebsiteTabs";
import { semanticColor } from "@how/design-system";

const columns: ColumnDef<FormRow>[] = [
  {
    accessorKey: "name",
    header: "Form",
    enableSorting: false,
    cell: (ctx) => (
      <Stack gap={0}>
        <Text size="sm" fw={600}>
          {ctx.row.original.name}
        </Text>
        {ctx.row.original.description === null ? null : (
          <Text size="xs" c="dimmed">
            {ctx.row.original.description}
          </Text>
        )}
      </Stack>
    ),
  },
  {
    id: "fields",
    header: "Questions",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) => <Text size="sm">{formFields(ctx.row.original.fields).length}</Text>,
  },
  {
    id: "submissions",
    header: "Received",
    enableSorting: false,
    cell: (ctx) => (
      <Group gap="xs">
        <Text size="sm">{ctx.row.original.submissions}</Text>
        {ctx.row.original.unread > 0 ? (
          <Badge variant="light" color={semanticColor("info")} size="md">
            {ctx.row.original.unread} unread
          </Badge>
        ) : null}
      </Group>
    ),
  },
  {
    id: "status",
    header: "Status",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.is_active ? (
        <Badge variant="light" color="teal">
          Taking messages
        </Badge>
      ) : (
        <Badge variant="light" color="gray">
          Closed
        </Badge>
      ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };

/** The church's forms. A form is placed on a page by adding a contact or newsletter block that points at it. */
export function FormsPage() {
  const navigate = useNavigate();
  const forms = useForms();
  const canManage = usePermission("forms.manage", { write: true });
  const createForm = useCreateForm();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const status = forms.isPending ? "loading" : forms.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="Website"
        description="Forms your church can put on a page, and what visitors have sent."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setAdding(true);
              }}
            >
              Add form
            </Button>
          ) : undefined
        }
      />
      <WebsiteTabs active="forms" />
      <DataTable
        label="Forms"
        columns={columns}
        rows={forms.data ?? []}
        totalRows={forms.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          void navigate(`/website/forms/${row.id}`);
        }}
        emptyTitle="No forms yet"
        emptyDescription="A contact form lets visitors write to the church without publishing an email address."
        errorMessage="Could not load forms."
        onRetry={() => {
          void forms.refetch();
        }}
      />
      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
          setName("");
        }}
        title="Add a form"
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            description="What staff call it. Visitors see the heading on the block instead."
            required
            value={name}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setName(next);
            }}
          />
          <Group justify="flex-end" mt="xs">
            <Button
              variant="subtle"
              onClick={() => {
                setAdding(false);
                setName("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={name.trim().length === 0}
              loading={createForm.isPending}
              onClick={() => {
                createForm.mutate(
                  { name: name.trim() },
                  {
                    onSuccess: (id) => {
                      notifications.show({
                        color: "teal",
                        title: "Form added",
                        message: name.trim(),
                      });
                      setAdding(false);
                      setName("");
                      void navigate(`/website/forms/${id}`);
                    },
                  },
                );
              }}
            >
              Add form
            </Button>
          </Group>
          {createForm.isError ? (
            <Text size="sm" c="red">
              The form was not added. Check your access and try again.
            </Text>
          ) : null}
        </Stack>
      </Modal>
    </>
  );
}
